import React, { useContext } from "react";
import { EditorInfoContext } from "../../../../../../../utils/userInfoContext";
import PreviewSizeBlock from "../../PreviewSizeBlock";
import { shownNumber } from "./sectionSummaries";

const PRESETS = {
    image: ['Original', '16:9', '4:3', '1:1', 'Fill width'],
    video: ['16:9', '4:3', '9:16', 'Fill width'],
    embed: ['Fill width', '1:1'],
    text: ['Fill width'],
    hotspot: ['1:1'],
};

const RATIOS = { '16:9': 16 / 9, '4:3': 4 / 3, '9:16': 9 / 16, '1:1': 1 };

const ElementSizeBlock = ({ tool, handleChange, errorState, icon, imageUrl, tone, fill, onPickFile, presetSet, naturalSize, children }) => {
    const { pageDimensions } = useContext(EditorInfoContext);
    const g = tool?.geometry || {};
    const pw = Number(pageDimensions?.width) || 0;
    const ph = Number(pageDimensions?.height) || 0;
    const left = Number(g.left) || 0;
    const top = Number(g.top) || 0;
    const w = Number(g.width) || 0;
    const h = Number(g.height) || 0;
    const fits = left >= 0 && top >= 0 && left + w <= pw && top + h <= ph;
    const isImage = imageUrl !== undefined;

    const fire = (name, value, type) => handleChange({ target: { name, value, type, checked: value } });

    const applyBox = (targetW, targetH) => {
        let nw = targetW;
        let nh = targetH;
        if (pw > 0 && nw > pw) {
            nh = nh * (pw / nw);
            nw = pw;
        }
        if (ph > 0 && nh > ph) {
            nw = nw * (ph / nh);
            nh = ph;
        }
        nw = pw > 0 ? Math.min(Math.round(nw), Math.floor(pw)) : Math.round(nw);
        nh = ph > 0 ? Math.min(Math.round(nh), Math.floor(ph)) : Math.round(nh);
        const nl = pw > 0 ? Math.max(0, Math.min(Math.round(left), Math.floor(pw - nw))) : left;
        const nt = ph > 0 ? Math.max(0, Math.min(Math.round(top), Math.floor(ph - nh))) : top;
        const wasLocked = !!g.keep_proportions;
        if (wasLocked) fire('geometry.keep_proportions', false, 'checkbox');
        fire('geometry.left', nl);
        fire('geometry.top', nt);
        fire('geometry.width', nw);
        fire('geometry.height', nh);
        return wasLocked;
    };

    const applyPreset = (label) => {
        if (w <= 0 || h <= 0) return;
        if (RATIOS[label]) {
            applyBox(w, w / RATIOS[label]);
        } else if (label === 'Fill width' && pw > 0) {
            const wasLocked = applyBox(pw, h * (pw / w));
            if (wasLocked) fire('geometry.keep_proportions', true, 'checkbox');
        } else if (label === 'Original' && naturalSize?.width && naturalSize?.height) {
            const wasLocked = applyBox(naturalSize.width, naturalSize.height);
            if (wasLocked) fire('geometry.keep_proportions', true, 'checkbox');
        }
    };

    const presets = (PRESETS[presetSet] || [])
        .filter((label) => label !== 'Original' || (naturalSize?.width && naturalSize?.height))
        .filter((label) => label !== 'Fill width' || pw > 0)
        .map((label) => ({ label, onClick: () => applyPreset(label) }));

    return (
        <PreviewSizeBlock
            label={tool?.page ? `Page ${tool.page}` : 'Page'}
            box={{ width: w, height: h }}
            icon={icon}
            src={isImage ? imageUrl : null}
            file={isImage ? tool?.file : null}
            tone={tone}
            fill={fill}
            hideCanvas={presetSet === 'text'}
            embedSrc={presetSet === 'video' ? tool?.value?.video_link || null
                : presetSet === 'embed' && /^https:\/\/\S+$/i.test(tool?.value?.video_link || '') ? tool.value.video_link : null}
            embedKind={presetSet === 'embed' ? 'content' : 'video'}
            contentTransform={`rotate(${Number(tool?.rotate) || 0}deg) scaleX(${tool?.flip === -1 ? -1 : 1})`}
            opacity={Number.isFinite(parseFloat(tool?.transparency)) ? parseFloat(tool.transparency) : 1}
            onPickFile={onPickFile}
            width={{ name: 'geometry.width', value: shownNumber(g.width), onChange: handleChange }}
            height={{ name: 'geometry.height', value: shownNumber(g.height), onChange: handleChange }}
            lock={{ id: 'geometry.keep_proportions', name: 'geometry.keep_proportions', value: g.keep_proportions, checked: g.keep_proportions, onChange: handleChange }}
            presets={presets}
            fit={pw && ph && w > 0 && h > 0 ? { ok: fits, text: fits ? 'Fits the page' : 'Extends beyond the page edges' } : null}
            error={errorState?.dimensionError}
        >
            {children}
        </PreviewSizeBlock>
    );
};

export default ElementSizeBlock;
